import { useContext, useState } from 'react';
import { api } from '@/utils/api';
import { handleError } from '@/utils/error';
import dayjs from 'dayjs';
import { toTitleCase } from '@concord-platform/text-utils';
import { ConfigContext, WidgetStateContext } from '@/context';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/Dialog';
import { ComplianceRequest } from '@/types';
import { useError } from '@/components/ui/useError';
import { Alert } from '@/components/ui/Alert';
import { WarningIcon } from '@phosphor-icons/react';

type Props = {
  item: ComplianceRequest;
  theme: 'light' | 'dark' | 'brand' | 'classic';
};

export function PrivacyRequestItem({ item, theme }: Props) {
  const [open, setOpen] = useState<boolean>(false);

  const {
    consentSettingsSubmitButton,
    errorTitle,
    getComplianceRequestTitle,
    getComplianceRequestStatus,
    privacyRequestsGetDetailsButton,
    privacyRequestsRequestDetailsText,
    privacyRequestsRequestDetailsTitle,
    privacyRequestsRequestsError,
  } = useContext(ConfigContext);
  const { topRef } = useContext(WidgetStateContext);

  const { hasError, showError, hideError } = useError();

  async function sendComplianceRequestDetail() {
    if (!item || !item?.project?.id || !item?.id) {
      return;
    }
    const {
      project: { id: projectId },
      id,
    } = item;
    try {
      await api.compliance.sendComplianceRequestDetail(id, projectId);
      hideError();
      setOpen(false);
    } catch (err) {
      handleError(err);
      showError();
    }
  }

  return (
    <div
      className="border-card-border rounded-card border p-6"
      data-testid="cd-privacy-request-item"
    >
      <div className="space-y-5">
        <div className="flex flex-col items-start justify-between gap-2 xl:flex-row">
          <div
            className="space-y-2"
            data-testid="cd-privacy-request-item-title"
          >
            <p className="text-subheader text-left text-sm font-semibold">
              {toTitleCase(getComplianceRequestTitle(item))}
            </p>
            <p className="text-muted-foreground text-left text-xs font-medium">
              {dayjs.utc(item.requestDate).local().format('L')}
            </p>
          </div>
          <Badge
            className={`rounded-card border px-2 py-2 text-xs ${
              item?.status?.toLowerCase() === 'resolved'
                ? `bg-accent-transparent border-accent text-accent-foreground`
                : `bg-primary-transparent border-primary text-primary`
            }`}
            data-testid="cd-status-tag"
          >
            {getComplianceRequestStatus(item)}
          </Badge>
        </div>
        <div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                className="min-w-auto px-4 py-2"
                data-testid="cd-get-details-button"
                variant="pc-outline"
                onClick={() => setOpen(true)}
              >
                {privacyRequestsGetDetailsButton}
              </Button>
            </DialogTrigger>

            <DialogContent
              className={`${theme} bg-background border-pc-border text-muted-foreground sm:max-w-[425px]`}
              container={topRef}
            >
              <DialogHeader>
                <DialogTitle
                  className="text-header"
                  data-testid="cd-privacy-request-modal-title"
                >
                  {privacyRequestsRequestDetailsTitle}
                </DialogTitle>
              </DialogHeader>
              {privacyRequestsRequestDetailsText}
              {hasError && (
                <Alert
                  icon={<WarningIcon size={18} weight="fill" />}
                  subTitle={privacyRequestsRequestsError}
                  title={errorTitle}
                  variant="error"
                />
              )}
              <DialogFooter>
                <Button
                  data-testid="cd-submit-button"
                  onClick={async () => {
                    await sendComplianceRequestDetail();
                  }}
                >
                  {consentSettingsSubmitButton}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
